/**
 * Redux reducer for local content.
 * @module redux/reducers/content
 */

import update from "immutability-helper";
import { LOGIN_SUCCESS } from "../actions/auth";
import {
  SEARCH_TERMS_CHANGED,
  SORT_BY_CHANGED,
  FILTER_BY_CHANGED,
  REPLACEABLE_CATEGORIES_CHANGED,
  ACTIVE_VIEW_CHANGED,
  CONTEXT_CHANGED,
  CATEGORY_CHANGED,
  CATEGORIES_CHANGED,
  SUBMIT_CONTENT_REQUEST,
  RECEIVED_CONTENT,
  CONTENT_REQUEST_FAILED,
  REQUEST_CATEGORIES,
  RECEIVED_CATEGORIES,
  CATEGORIES_REQUEST_FAILED,
  REQUEST_FILTER_OPTIONS,
  RECEIVED_FILTER_OPTIONS,
  FILTER_OPTIONS_REQUEST_FAILED,
  TOGGLE_FAVORITE_SUCCESS,
  PAGE_CHANGED,
  QUEUE_CONTENT_REQUEST,
  RELOAD,
  CLEAR_CONTENT,
  RESTORE_STATE,
} from "./../actions/content";
import { CLEAR_ASSET } from "../actions/asset";
import {
  RECEIVED_CONTEXTS as ASSET_RECEIVED_CONTEXTS,
  CONTEXT_CHANGED as ASSET_CONTEXT_CHANGED,
  TOGGLE_DETAILS_VIEW,
} from "../actions/assets";
import { SELECT_REPLACEABLE as ASSET_SELECT_REPLACEABLE } from "../actions/asset";
import FilterOption from "../constants/filterOptions";
import SortOption from "../constants/sortOptions";
import { restoreCategories } from "../util/savedStateUtils";

const initialState = {
  assetType: "image",
  categoryId: -1,
  restrictCategory: false,
  replaceableCategories: [],
  activeView: 1,
  timeRange: -1,
  contextId: -1,
  searchTerms: "",
  maxContent: 50,
  filterBy: FilterOption.NONE,
  sortBy: SortOption.MOST_RECENT,
  categoryList: [],
  categoriesLoading: false,
  categoriesInitialized: false,
  contextList: [],
  filterList: [],
  filtersInitialized: false,
  contentList: {
    localcontent: {
      searchType: "localcontent",
      contentList: [],
      pageNumber: 1,
      totalPages: 1,
      maxContent: 50,
      needUpdate: false,
      loading: false,
      error: false,
    },
    trackcontent: {
      searchType: "trackcontent",
      contentList: [],
      pageNumber: 1,
      totalPages: 1,
      maxContent: 50,
      needUpdate: false,
      loading: false,
      error: false,
    },
  },
  contentLoading: false,
  contentRequestQueued: false,
  isDetailsView: false,
  needUpdate: false,
};

/**
 * Resets the pages of each contentList to 1
 */
const resetPageNumber = (contentList) => {
  let newList = {};
  Object.keys(contentList).forEach((key) => {
    newList = update(contentList, {
      [key]: {
        pageNumber: { $set: 1 },
      },
    });
  });
  return newList;
};

const ACTION_HANDLERS = {
  [LOGIN_SUCCESS]: (state) => ({
    ...state,
    //needUpdate: true
  }),
  [SEARCH_TERMS_CHANGED]: (state, action) => ({
    ...state,
    searchTerms: action.payload.searchTerms,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true,
  }),
  [SORT_BY_CHANGED]: (state, action) => ({
    ...state,
    sortBy: action.payload.sortBy,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true,
  }),
  [FILTER_BY_CHANGED]: (state, action) => ({
    ...state,
    filterBy: action.payload.filterBy,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true,
  }),
  /*[SEARCH_TYPE_CHANGED]: (state, action) => ({
    ...state,
    searchType: action.payload.searchType,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true
  }),*/
  [ACTIVE_VIEW_CHANGED]: (state, action) => ({
    ...state,
    activeView: action.payload.activeView,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true,
  }),
  [CONTEXT_CHANGED]: (state, action) => ({
    ...state,
    contextId: !isNaN(action.payload.contextId)
      ? parseInt(action.payload.contextId, 10)
      : -1,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true,
  }),
  [ASSET_CONTEXT_CHANGED]: (state, action) => ({
    ...state,
    contextId: !isNaN(action.payload.contextId)
      ? parseInt(action.payload.contextId, 10)
      : -1,
    contentList: resetPageNumber(state.contentList),
    needUpdate: true,
  }),
  [REQUEST_FILTER_OPTIONS]: (state) => ({
    ...state,
    filtersLoading: true,
  }),
  [RECEIVED_FILTER_OPTIONS]: (state, action) => ({
    ...state,
    filtersLoading: false,
    filterList: action.payload.filterList,
    filtersInitialized: true,
  }),
  [FILTER_OPTIONS_REQUEST_FAILED]: (state) => ({
    ...state,
    filtersLoading: false,
    filterList: null,
  }),
  [CATEGORY_CHANGED]: (state, action) => {
    const index = state.categoryList.findIndex(
      (x) => x.id === action.payload.groupId
    );
    if (index >= 0) {
      return update(state, {
        categoryList: {
          [index]: {
            categories: {
              $set: state.categoryList[index].categories.map((category) => ({
                ...category,
                selected:
                  category.id === parseInt(action.payload.categoryId, 10),
              })),
            },
          },
        },
        contentList: { $set: resetPageNumber(state.contentList) },
        needUpdate: { $set: true },
      });
    }
    return {
      ...state,
      contentList: resetPageNumber(state.contentList),
      needUpdate: true,
    };
  },
  [CATEGORIES_CHANGED]: (state, action) => {
    const index = state.categoryList.findIndex(
      (x) => x.id === action.payload.groupId
    );
    if (index >= 0) {
      return update(state, {
        categoryList: {
          [index]: {
            categories: {
              $set: state.categoryList[index].categories.map((category) => ({
                ...category,
                selected: action.payload.categoryIds.some(
                  (id) => id === category.id
                ),
              })),
            },
          },
        },
        needUpdate: { $set: true },
      });
    }
    return {
      ...state,
      contentList: resetPageNumber(state.contentList),
      needUpdate: true,
    };
  },
  [CLEAR_ASSET]: (state) => ({
    ...state,
    restrictCategory: false,
  }),
  [ASSET_SELECT_REPLACEABLE]: (state, action) => {
    const newCategory = !isNaN(action.payload.categoryId)
      ? parseInt(action.payload.categoryId, 10)
      : -1;
    const categoryList = state.categoryList.map((group) => ({
      ...group,
      categories: group.categories.map((c) => ({
        ...c,
        selected: c.id === newCategory,
      })),
    }));
    return {
      ...state,
      categoryId: newCategory,
      categoryList,
      contentList: resetPageNumber(state.contentList),
      restrictCategory: !!action.payload.restrictCategory,
      needUpdate:
        state.restrictCategory !== !!action.payload.restrictCategory ||
        state.categoryId !== newCategory,
    };
  },
  [SUBMIT_CONTENT_REQUEST]: (state, action) =>
    update(state, {
      contentList: {
        [action.payload.searchType]: {
          contentList: { $set: [] },
          loading: { $set: true },
          needUpdate: { $set: false },
        },
      },
      contentLoading: { $set: true },
      needUpdate: { $set: false },
    }),
  [QUEUE_CONTENT_REQUEST]: (state, action) => ({
    ...state,
    assetType: action.payload.assetType,
    needUpdate: false,
    contentRequestQueued: true,
  }),
  [RECEIVED_CONTENT]: (state, action) =>
    update(state, {
      contentList: {
        [action.payload.searchType]: {
          searchType: { $set: action.payload.searchType },
          contentList: { $set: action.payload.contentList },
          pageNumber: { $set: action.payload.pageNumber },
          totalPages: { $set: action.payload.totalPages },
          maxContent: { $set: state.maxContent },
          loading: { $set: false },
          error: { $set: false },
        },
      },
      contentLoading: { $set: false },
      contentRequestQueued: { $set: false },
    }),
  [CONTENT_REQUEST_FAILED]: (state, action) =>
    update(state, {
      // Todo: Maybe assign an error here
      contentList: {
        [action.payload.searchType]: {
          loading: { $set: false },
          needUpdate: { $set: false },
          error: { $set: true },
        },
      },
      contentRequestQueued: { $set: false },
      contentLoading: { $set: false },
      needUpdate: { $set: false },
    }),
  [ASSET_RECEIVED_CONTEXTS]: (state, action) => ({
    ...state,
    contextList: action.payload.contextList,
  }),
  [REQUEST_CATEGORIES]: (state) => ({
    ...state,
    categoryList: [],
    categoriesInitialized: false,
    categoriesLoading: true,
  }),
  [RECEIVED_CATEGORIES]: (state, action) => {
    let categoryList = action.payload.categoryList;
    categoryList = categoryList.map((group) => ({
      ...group,
      categories: group.categories.map((category) => ({
        ...category,
        selected: category.id === state.categoryId,
      })),
    }));
    return {
      ...state,
      categoriesLoading: false,
      categoryList,
      categoriesInitialized: true,
      needUpdate: true,
    };
  },
  [CATEGORIES_REQUEST_FAILED]: (state) => ({
    ...state,
    categoriesLoading: false,
  }),
  [TOGGLE_FAVORITE_SUCCESS]: (state, action) => {
    const index = state.contentList[
      action.payload.searchType
    ].contentList.findIndex((c) => c.id === action.payload.id);
    if (index >= 0) {
      return update(state, {
        contentList: {
          [action.payload.searchType]: {
            contentList: {
              [index]: {
                isFavorite: { $set: action.payload.favorite },
              },
            },
          },
        },
      });
    }
    return state;
  },
  [PAGE_CHANGED]: (state, action) =>
    update(state, {
      contentList: {
        [action.payload.searchType]: {
          pageNumber: { $set: action.payload.page },
          needUpdate: { $set: true },
        },
      },
    }),
  [RELOAD]: (state, action) =>
    update(state, {
      contentList: {
        [action.payload.searchType]: {
          pageNumber: { $set: 1 },
          needUpdate: { $set: true },
        },
      },
    }),
  [CLEAR_CONTENT]: (state, action) =>
    update(state, {
      contentList: {
        [action.payload.searchType]: {
          pageNumber: { $set: 1 },
          contentList: { $set: [] },
        },
      },
    }),
  [REPLACEABLE_CATEGORIES_CHANGED]: (state, action) => ({
    ...state,
    replaceableCategories: action.payload.replaceableCategories || [],
  }),
  [TOGGLE_DETAILS_VIEW]: (state) => ({
    ...state,
    isDetailsView: !state.isDetailsView,
  }),
  [RESTORE_STATE]: (state, action) => ({
    ...state,
    contextId: action.payload.contextId || state.contextId,
    categoryList: restoreCategories(
      state.categoryList,
      action.payload.categoryList
    ),
    filterBy: action.payload.filterBy || state.filterBy,
    assetType: action.payload.assetType || state.assetType,
    activeView: action.payload.activeView || state.activeView,
    timeRange: action.payload.timeRange || state.timeRange,
    needUpdate: true,
  }),
};

export default function content(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}



// WEBPACK FOOTER //
// ./app/reducers/content.js